import axios, { type AxiosResponse } from "axios";
// src/api/auth.ts
import { User } from "src/contexts/UserContext";
import { getApiBaseUrl } from "src/config/Endpoints";
import { WELCOME_ROUTES_BY_STATUS } from "src/routes/welcomeRoutes";
import { isValidEmail } from "src/utils/Email";

const API_BASE_URL = getApiBaseUrl();

/**************** LOGIN PAGE APIs ***************/

export const loginUser = async (email: string, password: string) => {
  const response = await axios.post(`${API_BASE_URL}/tenant-login`, {
    email,
    password,
  });
  return response.data;
};

export const handleGoogleOAuthCallback = async (
  code: string,
  state: string
) => {
  const response = await axios.post(`${API_BASE_URL}/auth/callback`, {
    code,
    state,
  });
  return response.data;
};

export const getGoogleLoginUrl = (): string => {
  return `${API_BASE_URL}/auth/google`;
};



export const storeUserData = (data: any, setUser: (user: User) => void) => {
  const userData: User = {
    api_key: data.api_key,
    user_id: data.user_id?.toString() ?? null,
    tenant_id: data.tenant_id?.toString() ?? null,
    role_id: data.role_id?.toString() ?? null,
    email: data.email ?? null,
    company_name: data.company_name ?? null,
    user_name: data.user_name ?? null,
    profile_image_url: data.profile_image_url ?? null,
    social_listening_access:
      typeof data.social_listening_access === "boolean"
        ? data.social_listening_access
        : null,
  };

  // Save in localStorage
  Object.entries(userData).forEach(([key, value]) => {
    if (value === undefined || value === null) {return;}
    localStorage.setItem(key, String(value));
  });
  sessionStorage.setItem("user_id", data.user_id);

  // Update Context immediately
  setUser(userData);
};

/**************** SHARED VALIDATION ***************/

export const validateEmail = (email: string): string =>
  isValidEmail(email) ? "" : "Please enter a valid email address.";

export const validatePassword = (password: string): string => {
  const passwordRegex = /^(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[A-Z]).{8,}$/;
  return passwordRegex.test(password)
    ? ""
    : "Password must be at least 8 characters long, with an uppercase letter, number, and special character (!@#$%^&*).";
};

/**************** SIGNUP PAGE APIs ***************/

export const signupUser = async (email: string, password: string) => {
  const response = await axios.post(`${API_BASE_URL}/tenant-signup`, {
    email,
    password,
  });
  return response.data;
};

/**************** REDIRECT LOGIC ***************/

export const redirectBasedOnStatus = (navigate: (path: string) => void) => {
  const status = localStorage.getItem("status");

  if (status === "1") {
    navigate("/dashboard");
    return;
  }

  const welcomeTarget = WELCOME_ROUTES_BY_STATUS[Number(status)];
  if (welcomeTarget) {
    navigate(welcomeTarget.pathname);
  }
};

/**************** OTP Screen ***************/

export const verifyOtp = async ({
  api_key,
  user_id,
  tenant_id,
  email,
  code,
}: {
  api_key?: string | null;
  user_id: string | null;
  tenant_id: string | null;
  email: string | null;
  code: string;
}) => {
  const response = await fetch(`${API_BASE_URL}/confirm-email`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({
      email,
      code,
      user_id,
      tenant_id,
    }),
  });

  return await response.json();
};

export const resendOtp = async (
  email: string | null,
  api_key: string | undefined
) => {
  const response = await fetch(
    `${API_BASE_URL}/send-confirmation-email?email=${email}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${api_key}`,
      },
    }
  );

  return await response.json();
};

/**************** ForgotPasswordEmail Screen ***************/
export const sendResetPasswordEmail = async (email: string): Promise<any> => {
  const apiKey = localStorage.getItem("api_key");
  const response = await fetch(
    `${API_BASE_URL}/auth/forgot-password?email=${encodeURIComponent(
      email
    )}&api_key=${encodeURIComponent(apiKey || "")}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return await response.json();
};

/**************** ResetPasswordNotification Screen ***************/
export const resendResetPasswordEmail = async (): Promise<any> => {
  const email = localStorage.getItem("email");
  const apiKey = localStorage.getItem("api_key");

  const response = await fetch(
    `${API_BASE_URL}/auth/forgot-password?email=${encodeURIComponent(
      email || ""
    )}&api_key=${encodeURIComponent(apiKey || "")}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  return await response.json();
};

/**************** MakeNewPassword Screen ***************/
export const resetUserPassword = async ({
  token,
  newPassword,
}: {
  token: string;
  newPassword: string;
}): Promise<any> => {
  const api_key = localStorage.getItem("api_key");

  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${api_key}`,
    },
    body: JSON.stringify({
      reset_token: token,
      new_password: newPassword,
    }),
  });

  return await response.json();
};

export const autoLoginLog = async (): Promise<boolean> => {
  const apiKey = localStorage.getItem("api_key");
  const userId = localStorage.getItem("user_id");

  if (!apiKey || !userId) {
    console.error("Missing api_key or user_id for auto-login log");
    return false;
  }

  try {
    const response: AxiosResponse<{
      status: boolean;
      message: string;
    }> = await axios.post(
      `${API_BASE_URL}/auto-login-log`,
      { user_id: Number(userId) },
      {
        headers: {
          Authorization: `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data.status;
  } catch (error) {
 
    return false;
  }
};
